import React, { useMemo, createContext, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useMedia } from 'react-use'
import { useBreakpoints } from '../../views/hooks'

import { layoutEvents } from './state/state'
import * as selectors from './state/selectors'

type PanelProps = { open: boolean; width?: number }

export interface LayoutInitialProps {
  sidebar?: PanelProps
  primaryPanel?: PanelProps
  secondaryPanel?: PanelProps
}

export interface LayoutContextValue {
  breakpoints: Record<string, number>
  sidebar: {
    open: boolean
    width: number
    openSidebar: () => void
    closeSidebar: () => void
  }
  primaryPanel: {
    open: boolean
    width: number
    openPrimaryPanel: () => void
    closePrimaryPanel: () => void
  }
  secondaryPanel: {
    isOpen: boolean
    width: number
    open: () => void
    close: () => void
  }
}

interface LayoutProviderProps {
  stateKey: string
  initialProps?: LayoutInitialProps
  children?: React.ReactNode
}

export const LayoutContext = createContext<LayoutContextValue>({} as LayoutContextValue)

const LayoutProvider = ({ stateKey, initialProps: initialPropsProp, children }: LayoutProviderProps) => {
  const dispatch = useDispatch()
  const { breakpoints, isMobile } = useBreakpoints()

  const gtmd = useMedia(`(min-width: ${breakpoints.md}px)`)

  const initialProps: LayoutInitialProps = initialPropsProp ?? {
    sidebar: { open: !isMobile, width: gtmd ? 240 : window.outerWidth },
    primaryPanel: { open: false, width: 600 },
    secondaryPanel: { open: false, width: 375 },
  }

  const layoutStateByKey = useSelector(state => selectors.getLayoutByKey(state)(stateKey))
  useEffect(() => {
    if (stateKey && !layoutStateByKey) {
      dispatch(layoutEvents.init({ key: stateKey, initialState: initialProps }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey, layoutStateByKey])

  const isSidebarOpen = useSelector(state => selectors.isSidebarOpen(state)(stateKey))
  const openSidebar = useCallback(() => dispatch(layoutEvents.openSidebar(stateKey)), [dispatch, stateKey])
  const closeSidebar = useCallback(() => dispatch(layoutEvents.closeSidebar(stateKey)), [dispatch, stateKey])

  const isPrimaryPanelOpen = useSelector(state => selectors.isPrimaryPanelOpen(state)(stateKey))
  const openPrimaryPanel = useCallback(() => dispatch(layoutEvents.openPrimaryPanel(stateKey)), [dispatch, stateKey])
  const closePrimaryPanel = useCallback(() => dispatch(layoutEvents.closePrimaryPanel(stateKey)), [dispatch, stateKey])

  const isSecondaryPanelOpen = useSelector(state => selectors.isSecondaryPanelOpen(state)(stateKey))
  const openSecondaryPanel = useCallback(
    () => dispatch(layoutEvents.openSecondaryPanel(stateKey)),
    [dispatch, stateKey],
  )
  const closeSecondaryPanel = useCallback(
    () => dispatch(layoutEvents.closeSecondaryPanel(stateKey)),
    [dispatch, stateKey],
  )

  const layoutStore = useMemo<LayoutContextValue>(
    () => ({
      breakpoints,
      sidebar: {
        width: initialProps.sidebar?.width ?? 240,
        open: isSidebarOpen,
        openSidebar,
        closeSidebar,
      },
      primaryPanel: {
        width: initialProps.primaryPanel?.width ?? 600,
        open: isPrimaryPanelOpen,
        openPrimaryPanel,
        closePrimaryPanel,
      },
      secondaryPanel: {
        width: initialProps.secondaryPanel?.width ?? 375,
        isOpen: isSecondaryPanelOpen,
        open: openSecondaryPanel,
        close: closeSecondaryPanel,
      },
    }),
    [
      breakpoints,
      initialProps,
      isSidebarOpen,
      openSidebar,
      closeSidebar,
      isPrimaryPanelOpen,
      openPrimaryPanel,
      closePrimaryPanel,
      isSecondaryPanelOpen,
      openSecondaryPanel,
      closeSecondaryPanel,
    ],
  )

  return <LayoutContext.Provider value={layoutStore}>{children}</LayoutContext.Provider>
}

export default LayoutProvider
